/**
 * Safe expression parser for eval: expressions
 * 
 * SECURITY: Does NOT use Function/eval. Only allows whitelisted operations.
 * 
 * Supported patterns (based on real ERPNext usage):
 * - doc.field == "value"
 * - doc.field != "value"
 * - doc.field (truthy check)
 * - doc.field && doc.other == 1
 * - doc.field || doc.other == 1
 * - !doc.field
 * - doc.field > 0, doc.field < 100, doc.field >= 0, doc.field <= 100
 * - parent.field (parent document references)
 * 
 * Grammar:
 * expression := orExpr
 * orExpr := andExpr ('||' andExpr)*
 * andExpr := notExpr ('&&' notExpr)*
 * notExpr := '!' notExpr | comparison
 * comparison := term (('==' | '!=' | '>' | '<' | '>=' | '<=') term)?
 * term := '(' orExpr ')' | 'doc.' identifier | 'parent.' identifier | string | number | boolean
 */

type ASTNode = 
  | { type: 'or'; left: ASTNode; right: ASTNode }
  | { type: 'and'; left: ASTNode; right: ASTNode }
  | { type: 'not'; expr: ASTNode }
  | { type: 'eq'; left: ASTNode; right: ASTNode }
  | { type: 'ne'; left: ASTNode; right: ASTNode }
  | { type: 'gt'; left: ASTNode; right: ASTNode }
  | { type: 'lt'; left: ASTNode; right: ASTNode }
  | { type: 'gte'; left: ASTNode; right: ASTNode }
  | { type: 'lte'; left: ASTNode; right: ASTNode }
  | { type: 'ref'; source: 'doc' | 'parent'; field: string }
  | { type: 'literal'; value: string | number | boolean };

/**
 * Parse and evaluate a safe expression
 * 
 * @param expr - Expression string (without "eval:" prefix)
 * @param context - Document context with doc and optional parent
 * @returns Boolean result
 * @throws Error if expression is invalid
 */
export function parseAndEvaluate(
  expr: string,
  context: { doc: Record<string, any>; parent?: Record<string, any> }
): boolean {
  try {
    const tokens = tokenize(expr);
    const ast = parseExpression(tokens);
    return evaluateAST(ast, context);
  } catch (error) {
    console.error("[safeExpressionParser] Error parsing expression:", expr, error);
    throw new Error(`Invalid expression: ${expr}`);
  }
}

/**
 * Tokenize expression string into tokens
 * Handles: identifiers, operators, string literals, numbers, whitespace
 */
function tokenize(expr: string): string[] {
  const tokens: string[] = [];
  let i = 0;
  
  while (i < expr.length) {
    // Skip whitespace
    if (/\s/.test(expr[i])) {
      i++;
      continue;
    }
    
    // Operators (multi-character first — check 3-char before 2-char)
    if (expr.slice(i, i + 3) === '===') {
      tokens.push('=='); // Treat === same as == (no type distinction in our evaluator)
      i += 3;
      continue;
    }
    if (expr.slice(i, i + 3) === '!==') {
      tokens.push('!='); // Treat !== same as !=
      i += 3;
      continue;
    }
    if (expr.slice(i, i + 2) === '==') {
      tokens.push('==');
      i += 2;
      continue;
    }
    if (expr.slice(i, i + 2) === '!=') {
      tokens.push('!=');
      i += 2;
      continue;
    }
    if (expr.slice(i, i + 2) === '>=') {
      tokens.push('>=');
      i += 2;
      continue;
    }
    if (expr.slice(i, i + 2) === '<=') {
      tokens.push('<=');
      i += 2;
      continue;
    }
    if (expr.slice(i, i + 2) === '&&') {
      tokens.push('&&');
      i += 2;
      continue;
    }
    if (expr.slice(i, i + 2) === '||') {
      tokens.push('||');
      i += 2;
      continue;
    }
    
    // Parentheses (grouping)
    if (expr[i] === '(' || expr[i] === ')') {
      tokens.push(expr[i]);
      i++;
      continue;
    }

    // Single character operators
    if (['>', '<', '!', '&', '|'].includes(expr[i])) {
      tokens.push(expr[i]);
      i++;
      continue;
    }
    
    // String literals (single or double quotes)
    if (expr[i] === '"' || expr[i] === "'") {
      const quote = expr[i];
      i++;
      let str = quote; // Start with quote to mark as string literal
      while (i < expr.length && expr[i] !== quote) {
        if (expr[i] === '\\') {
          str += expr[i]; // Include backslash
          i++;
          if (i < expr.length) {
            str += expr[i];
            i++;
          }
        } else {
          str += expr[i];
          i++;
        }
      }
      if (i < expr.length) {
        str += quote; // Add closing quote
        tokens.push(str);
        i++; // skip closing quote
      }
      continue;
    }
    
    // Numbers
    if (/\d/.test(expr[i])) {
      let num = '';
      while (i < expr.length && /[\d.]/.test(expr[i])) {
        num += expr[i];
        i++;
      }
      tokens.push(num);
      continue;
    }
    
    // Identifiers (doc.fieldname, parent.fieldname, or fieldname)
    if (/[a-zA-Z_]/.test(expr[i])) {
      let ident = '';
      while (i < expr.length && /[a-zA-Z0-9_.]/.test(expr[i])) {
        ident += expr[i];
        i++;
      }
      tokens.push(ident);
      continue;
    }
    
    // Unknown character
    throw new Error(`Unexpected character: ${expr[i]}`);
  }
  
  return tokens;
}

/**
 * Parse tokens into AST using recursive descent
 */
function parseExpression(tokens: string[]): ASTNode {
  let index = 0;
  
  function parseOrExpr(): ASTNode {
    let left = parseAndExpr();
    while (index < tokens.length && tokens[index] === '||') {
      index++;
      const right = parseAndExpr();
      left = { type: 'or', left, right };
    }
    return left;
  }
  
  function parseAndExpr(): ASTNode {
    let left = parseNotExpr();
    while (index < tokens.length && tokens[index] === '&&') {
      index++;
      const right = parseNotExpr();
      left = { type: 'and', left, right };
    }
    return left;
  }
  
  function parseNotExpr(): ASTNode {
    if (index < tokens.length && tokens[index] === '!') {
      index++;
      return { type: 'not', expr: parseNotExpr() };
    }
    return parseComparison();
  }
  
  function parseComparison(): ASTNode {
    const left = parseTerm();
    
    if (index < tokens.length) {
      const op = tokens[index];
      if (op === '==' || op === '!=' || op === '>' || op === '<' || op === '>=' || op === '<=') {
        index++;
        const right = parseTerm();
        return { 
          type: op === '==' ? 'eq' : op === '!=' ? 'ne' : op === '>' ? 'gt' : op === '<' ? 'lt' : op === '>=' ? 'gte' : 'lte', 
          left, 
          right 
        };
      }
    }
    
    // No comparison operator - treat as truthy check
    return left;
  }
  
  function parseTerm(): ASTNode {
    if (index >= tokens.length) {
      throw new Error('Unexpected end of expression');
    }

    const token = tokens[index];

    // Parenthesized group
    if (token === '(') {
      index++; // consume '('
      const expr = parseOrExpr();
      if (index >= tokens.length || tokens[index] !== ')') {
        throw new Error('Expected closing parenthesis');
      }
      index++; // consume ')'
      return expr;
    }

    // String literal (includes quotes)
    if ((token.startsWith('"') && token.endsWith('"')) || (token.startsWith("'") && token.endsWith("'"))) {
      index++;
      // Remove quotes
      const value = token.slice(1, -1);
      return { type: 'literal', value };
    }
    
    // Number
    if (/^\d/.test(token)) {
      index++;
      return { type: 'literal', value: Number(token) };
    }
    
    // Boolean
    if (token === 'true') {
      index++;
      return { type: 'literal', value: true };
    }
    if (token === 'false') {
      index++;
      return { type: 'literal', value: false };
    }
    
    // Reference: doc.fieldname or parent.fieldname
    if (token.startsWith('doc.')) {
      index++;
      const field = token.slice(4);
      return { type: 'ref', source: 'doc', field };
    }
    if (token.startsWith('parent.')) {
      index++;
      const field = token.slice(7);
      return { type: 'ref', source: 'parent', field };
    }
    
    // Plain identifier (treat as doc.fieldname)
    if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(token)) {
      index++;
      return { type: 'ref', source: 'doc', field: token };
    }
    
    throw new Error(`Unexpected token: ${token}`);
  }
  
  const ast = parseOrExpr();
  if (index < tokens.length) {
    throw new Error(`Unexpected token at end: ${tokens[index]}`);
  }
  return ast;
}

/**
 * Evaluate AST safely
 */
function evaluateAST(
  node: ASTNode,
  context: { doc: Record<string, any>; parent?: Record<string, any> }
): boolean {
  switch (node.type) {
    case 'or':
      return evaluateAST(node.left, context) || evaluateAST(node.right, context);
    
    case 'and':
      return evaluateAST(node.left, context) && evaluateAST(node.right, context);
    
    case 'not':
      return !evaluateAST(node.expr, context);
    
    case 'eq':
      return getValue(node.left, context) === getValue(node.right, context);
    
    case 'ne':
      return getValue(node.left, context) !== getValue(node.right, context);
    
    case 'gt':
      return Number(getValue(node.left, context)) > Number(getValue(node.right, context));
    
    case 'lt':
      return Number(getValue(node.left, context)) < Number(getValue(node.right, context));
    
    case 'gte':
      return Number(getValue(node.left, context)) >= Number(getValue(node.right, context));
    
    case 'lte':
      return Number(getValue(node.left, context)) <= Number(getValue(node.right, context));
    
    case 'ref':
      const source = node.source === 'doc' ? context.doc : context.parent;
      if (!source) return false;
      const value = source[node.field];
      return Array.isArray(value) ? value.length > 0 : !!value;
    
    case 'literal':
      return !!node.value;
    
    default:
      throw new Error(`Unknown AST node type: ${(node as any).type}`);
  }
}

function getValue(
  node: ASTNode,
  context: { doc: Record<string, any>; parent?: Record<string, any> }
): any {
  if (node.type === 'ref') {
    const source = node.source === 'doc' ? context.doc : context.parent;
    return source ? source[node.field] : undefined;
  }
  if (node.type === 'literal') {
    return node.value;
  }
  // For comparison operations, evaluate as boolean then convert
  return evaluateAST(node, context);
}

